/**
 * @file Builds the residence-state description rows of an investment profile.
 */
import React from "react";

import { Value } from "@components/Text";
import { getRegionNameByCountryAndRegionCode } from "@components/Address";

import renderBoolean from "./renderBoolean";
import type { DescriptionItem, ResidenceProfile } from "./types";

type Labels = {
  isUsPerson: string;
  isUsCitizen: string;
};

const LABEL_STATE = "State of Residence";
const LABEL_NOT_US_PERSON = "Not a resident of US";

const DEFAULT_LABELS: Labels = {
  isUsPerson: "Are you a US person?",
  isUsCitizen: "Are you a US citizen?",
};

/**
 * Builds the description rows for the US-citizen, US-person and residence-state answers of a profile.
 */
const renderResidenceState = (
  profile: ResidenceProfile,
  addressCountry?: string,
  addressRegion?: string,
  labels: Partial<Labels> = {},
): DescriptionItem[] => {
  const mergedLabels: Labels = { ...DEFAULT_LABELS, ...labels };

  const { isUsPerson, isUsCitizen } = profile;

  const regionName = getRegionNameByCountryAndRegionCode(addressCountry, addressRegion);

  const items: DescriptionItem[] = [
    {
      label: mergedLabels.isUsCitizen,
      value: renderBoolean(isUsCitizen),
    },
  ];

  const isNotUsCitizen = isUsCitizen === false;

  if (isNotUsCitizen) {
    items.push({
      label: mergedLabels.isUsPerson,
      value: renderBoolean(isUsPerson),
    });
  }

  if (isUsPerson) {
    return [
      {
        label: LABEL_STATE,
        value: <Value value={regionName} />,
      },
    ];
  }

  const isNotUsPerson = isUsPerson === false;

  if (isNotUsPerson) {
    items.push({
      label: LABEL_STATE,
      value: LABEL_NOT_US_PERSON,
    });
  }

  return items;
};

export default renderResidenceState;
